import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ARCA_ENVIRONMENTS,
  ARCA_IVA_CONDITIONS,
  ARCA_OPERATOR_ROLES,
  FISCAL_CONTACT_LIMITS,
  FISCAL_DELEGATION_STATUS,
  buildPointOfSaleDrafts,
  createRestaurantFiscalDraft,
  formatCuit,
  normalizeCuit,
  normalizePointOfSale,
  validateRestaurantFiscalDraft,
  validateFiscalContact,
} from "../../services/local/fiscalConnection";
import { getFiscalOnboardingProgress } from "../../services/local/fiscalOnboardingProgress";
import { getCurrentFiscalReview } from "../../services/local/fiscalReview";
import ArcaDelegationGuide from "./ArcaDelegationGuide";
import {
  getRestaurantFiscalConnections,
  requestRestaurantFiscalConnectionVerification,
  saveRestaurantFiscalIssuerDraft,
  saveRestaurantFiscalPointOfSale,
} from "../../services/supabase/fiscalConnectionService";

const getStatusStyle = (tone) => {
  if (tone === "success") {
    return styles.statusSuccess;
  }

  if (tone === "pending") {
    return styles.statusPending;
  }

  if (tone === "danger") {
    return styles.statusDanger;
  }

  return styles.statusNeutral;
};

const formatConnectionDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const EMPTY_BRANCHES = [];

function ArcaFiscalConnectionPanelContent({
  companyId,
  companyProfile,
  branches = EMPTY_BRANCHES,
  canManage = false,
  currentAccessRoleLabel = "Este rol",
  previewMode = false,
}) {
  const [environment, setEnvironment] = useState("production");
  const [connections, setConnections] = useState([]);
  const [fiscalDraft, setFiscalDraft] = useState(() =>
    createRestaurantFiscalDraft({ companyProfile })
  );
  const [pointDrafts, setPointDrafts] = useState({});
  const [delegationConfirmed, setDelegationConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [draftDirty, setDraftDirty] = useState(false);
  const dirtyRef = useRef(false);
  const draftScopeRef = useRef("");
  const issuerHeadingRef = useRef(null);
  const issuerInputRef = useRef(null);
  const loadSequence = useRef(0);

  const selectedConnection = useMemo(
    () => connections.find((connection) => connection.companyId === companyId && connection.environment === environment) || null,
    [connections, environment, companyId]
  );
  const progress = getFiscalOnboardingProgress(selectedConnection, branches);
  const activeBranches = useMemo(
    () => branches.filter((branch) => branch.status !== "inactive"),
    [branches]
  );

  const loadConnections = useCallback(async () => {
    if (previewMode) {
      setConnections([]);
      setErrorMessage("");
      setLoading(false);
      return;
    }

    if (!companyId) {
      setConnections([]);
      return;
    }

    setLoading(true);
    const sequence = ++loadSequence.current;

    try {
      const nextConnections = await getRestaurantFiscalConnections(companyId);
      if (sequence !== loadSequence.current) return;
      setConnections(nextConnections);
      setErrorMessage("");
    } catch (error) {
      if (sequence !== loadSequence.current) return;
      setErrorMessage(
        error.message || "No se pudo cargar el estado de la conexion fiscal."
      );
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [companyId, previewMode]);

  useEffect(() => {
    loadConnections();
    return () => { loadSequence.current += 1; };
  }, [loadConnections]);

  useEffect(() => {
    const scope = `${companyId}:${environment}`;
    if (draftScopeRef.current !== scope || !dirtyRef.current) {
      draftScopeRef.current = scope;
      dirtyRef.current = false;
      setDraftDirty(false);
      setFiscalDraft(createRestaurantFiscalDraft({ companyProfile, issuer: selectedConnection }));
    }
  }, [companyId, environment, companyProfile, selectedConnection]);

  useEffect(() => {
    setPointDrafts(
      buildPointOfSaleDrafts(activeBranches, selectedConnection?.pointsOfSale || [])
    );
    setDelegationConfirmed(
      selectedConnection?.delegationStatus === FISCAL_DELEGATION_STATUS.PENDING ||
        selectedConnection?.delegationStatus === FISCAL_DELEGATION_STATUS.VERIFIED
    );
  }, [activeBranches, selectedConnection]);

  const openIssuerForm = () => {
    issuerHeadingRef.current?.scrollIntoView?.({ block: "start", behavior: "auto" });
    const target = canManage && !loading && !busyAction ? issuerInputRef.current : issuerHeadingRef.current;
    target?.focus({ preventScroll: true });
  };

  const showError = (message) => {
    setSuccessMessage("");
    setErrorMessage(message);
  };

  const showSuccess = (message) => {
    setErrorMessage("");
    setSuccessMessage(message);
  };

  const handleEnvironmentChange = (nextEnvironment) => {
    if (busyAction) {
      return;
    }

    setEnvironment(nextEnvironment);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleFiscalDraftChange = (field, value) => {
    dirtyRef.current = true;
    setDraftDirty(true);
    setFiscalDraft((currentDraft) => ({
      ...currentDraft,
      [field]: field === "cuit" ? normalizeCuit(value) : value,
      ...(field === "ivaConditionId"
        ? {
            ivaConditionLabel:
              ARCA_IVA_CONDITIONS.find(
                (condition) => condition.id === Number(value)
              )?.label || "",
          }
        : {}),
    }));
  };

  const handleSaveIssuer = async () => {
    if (!canManage) {
      showError(
        `${currentAccessRoleLabel} puede revisar la conexion, pero solo el dueno o admin general puede modificarla.`
      );
      return;
    }

    const validationMessage = validateRestaurantFiscalDraft(fiscalDraft) || validateFiscalContact(fiscalDraft);

    if (validationMessage) {
      showError(validationMessage);
      return;
    }

    setBusyAction("issuer");

    try {
      const savedIssuer = await saveRestaurantFiscalIssuerDraft(companyId, {
        ...fiscalDraft,
        environment,
      });
      dirtyRef.current = false;
      setDraftDirty(false);
      setFiscalDraft(createRestaurantFiscalDraft({ issuer: savedIssuer }));
      setConnections((current) => [...current.filter((item) => item.environment !== environment), {
        ...savedIssuer, pointsOfSale: selectedConnection?.pointsOfSale || [],
      }]);
      showSuccess("Datos fiscales y de contacto guardados. Esto no informa la delegacion ni activa la facturacion.");
    } catch (error) {
      showError(error.message || "No se pudieron guardar los datos fiscales.");
    } finally {
      setBusyAction("");
    }
  };

  const handlePointDraftChange = (branchId, field, value) => {
    setPointDrafts((currentDrafts) => ({
      ...currentDrafts,
      [branchId]: {
        ...(currentDrafts[branchId] || {}),
        [field]: field === "pointOfSale" ? `${value || ""}`.replace(/\D/g, "") : value,
      },
    }));
  };

  const handleSavePointOfSale = async (branch) => {
    if (!canManage) {
      showError(
        `${currentAccessRoleLabel} no puede modificar puntos de venta fiscales.`
      );
      return;
    }

    if (!selectedConnection?.id) {
      showError("Primero guarda los datos fiscales del restaurante.");
      return;
    }

    const draft = pointDrafts[branch.id] || {};
    const pointOfSale = normalizePointOfSale(draft.pointOfSale);

    if (!pointOfSale) {
      showError(`Ingresa un punto de venta valido para ${branch.name || "la sucursal"}.`);
      return;
    }

    setBusyAction(`point:${branch.id}`);

    try {
      await saveRestaurantFiscalPointOfSale(companyId, {
        environment,
        branchId: branch.id,
        pointOfSale,
        label: draft.label,
      });
      await loadConnections();
      showSuccess(`Punto de venta de ${branch.name || "la sucursal"} guardado.`);
    } catch (error) {
      showError(error.message || "No se pudo guardar el punto de venta.");
    } finally {
      setBusyAction("");
    }
  };

  const handleRequestVerification = async () => {
    if (!canManage) {
      showError(
        `${currentAccessRoleLabel} no puede solicitar la verificacion fiscal.`
      );
      return;
    }

    if (draftDirty) {
      showError("Guarda primero los cambios de Datos del emisor antes de solicitar una revision.");
      return;
    }

    if (!selectedConnection?.id) {
      showError("Primero guarda los datos fiscales del restaurante.");
      return;
    }

    if (!delegationConfirmed) {
      showError("Confirma que completaste la delegacion en ARCA.");
      return;
    }

    const configuredBranchIds = new Set(
      (selectedConnection.pointsOfSale || []).map((point) => point.branchId)
    );
    const missingBranches = activeBranches.filter(
      (branch) => !configuredBranchIds.has(branch.id)
    );

    if (missingBranches.length) {
      showError(
        `Falta guardar el punto de venta de: ${missingBranches
          .map((branch) => branch.name || "Sucursal")
          .join(", ")}.`
      );
      return;
    }

    setBusyAction("verification");

    try {
      await requestRestaurantFiscalConnectionVerification(companyId, environment);
      await loadConnections();
      showSuccess(
        "Aviso registrado en la bandeja de MozzoApp. Espera la revision del equipo; esto no verifica ARCA ni activa facturacion."
      );
    } catch (error) {
      showError(error.message || "No se pudo solicitar la verificacion fiscal.");
    } finally {
      setBusyAction("");
    }
  };

  const currentReview = getCurrentFiscalReview(selectedConnection);
  const pendingReview = selectedConnection?.delegationStatus === FISCAL_DELEGATION_STATUS.PENDING && Boolean(selectedConnection?.metadata?.verificationRequestedAt) && currentReview?.status !== "requires_correction";
  const connectionMessage = currentReview?.message || (["verified", "error", "revoked"].includes(selectedConnection?.delegationStatus) ? selectedConnection?.metadata?.verificationMessage : "") || "";
  const connectedAt = formatConnectionDate(selectedConnection?.connectedAt);

  return (
    <section style={styles.panel} aria-label="Conexion fiscal ARCA">
      <div style={styles.header}>
        <div style={styles.headerCopy}>
          <span style={styles.eyebrow}>Conexion fiscal del restaurante</span>
          <strong style={styles.title}>Vincular Facturacion Electronica</strong>
          <span style={styles.description}>
            El restaurante conserva su CUIT. Solo delega el servicio a MozzoApp y puede
            revocarlo desde ARCA cuando quiera.
          </span>
        </div>
        <span style={{ ...styles.status, ...getStatusStyle(progress.tone) }}>
          {loading ? "Consultando..." : progress.summary}
        </span>
      </div>

      <div style={styles.securityNotice}>
        <strong>MozzoApp nunca solicita la clave fiscal.</strong>
        <span>
          Tampoco guardamos el certificado privado del restaurante. La autorizacion se
          realiza dentro del sitio oficial de ARCA.
        </span>
      </div>

      {previewMode ? (
        <div style={styles.previewNotice}>
          <strong>Vista de demostracion.</strong>
          <span>
            Puedes recorrer el circuito completo. Para guardar el CUIT, los puntos de
            venta o solicitar la verificacion, ingresa como dueno o admin general del
            restaurante.
          </span>
        </div>
      ) : null}

      {(errorMessage || successMessage) && (
        <div
          role={errorMessage ? "alert" : "status"}
          style={{
            ...styles.message,
            ...(errorMessage ? styles.messageError : styles.messageSuccess),
          }}
        >
          {errorMessage || successMessage}
        </div>
      )}

      <div style={styles.environmentRow}>
        {ARCA_ENVIRONMENTS.map((option) => {
          const isSelected = environment === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleEnvironmentChange(option.value)}
              style={{
                ...styles.environmentButton,
                ...(isSelected ? styles.environmentButtonActive : {}),
              }}
              aria-pressed={isSelected}
            >
              <strong>{option.label}</strong>
              <span>{option.description}</span>
            </button>
          );
        })}
      </div>

      <div style={styles.step} id="arca-issuer">
        <div style={styles.stepHeader}>
          <span style={styles.stepNumber}>1</span>
          <div>
            <h3 ref={issuerHeadingRef} tabIndex={-1} style={{ ...styles.stepTitle, margin: 0, scrollMarginTop: "80px" }}>Datos del emisor</h3>
            <p style={styles.stepDescription}>
              Deben coincidir con la constancia de inscripcion del restaurante.
            </p>
          </div>
        </div>

        <div style={styles.formGrid}>
          <label style={styles.fieldLabel}>
            CUIT
            <input
              type="text"
              inputMode="numeric"
              value={formatCuit(fiscalDraft.cuit)}
              onChange={(event) => handleFiscalDraftChange("cuit", event.target.value)}
              placeholder="11 digitos"
              style={styles.input}
              ref={issuerInputRef}
              maxLength={13}
              disabled={!canManage || loading || Boolean(busyAction)}
            />
          </label>
          <label style={styles.fieldLabel}>
            Razon social
            <input
              type="text"
              value={fiscalDraft.legalName}
              onChange={(event) =>
                handleFiscalDraftChange("legalName", event.target.value)
              }
              placeholder="Razon social registrada"
              style={styles.input}
              maxLength={200}
              disabled={!canManage || loading || Boolean(busyAction)}
            />
          </label>
          <label style={styles.fieldLabel}>
            Condicion frente al IVA
            <select
              value={fiscalDraft.ivaConditionId}
              onChange={(event) =>
                handleFiscalDraftChange("ivaConditionId", event.target.value)
              }
              style={styles.input}
              disabled={!canManage || loading || Boolean(busyAction)}
            >
              <option value="">Seleccionar</option>
              {ARCA_IVA_CONDITIONS.map((condition) => (
                <option key={condition.id} value={condition.id}>
                  {condition.label}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.fieldLabel}>
            Ingresos Brutos
            <input
              type="text"
              value={fiscalDraft.grossIncomeNumber}
              onChange={(event) =>
                handleFiscalDraftChange("grossIncomeNumber", event.target.value)
              }
              placeholder="Opcional si no corresponde"
              style={styles.input}
              maxLength={80}
              disabled={!canManage || loading || Boolean(busyAction)}
            />
          </label>
          <label style={{ ...styles.fieldLabel, gridColumn: "1 / -1" }}>
            Domicilio fiscal
            <input
              type="text"
              value={fiscalDraft.fiscalAddress}
              onChange={(event) =>
                handleFiscalDraftChange("fiscalAddress", event.target.value)
              }
              placeholder="Domicilio declarado ante ARCA"
              style={styles.input}
              maxLength={500}
              disabled={!canManage || loading || Boolean(busyAction)}
            />
          </label>
        </div>

        <div style={styles.stepDescription}>El restaurante emite la factura al cliente. MozzoApp factura su propio servicio al restaurante por separado.</div>
        <h4 style={{ ...styles.stepTitle, margin: 0 }}>Contacto y persona responsable del tramite</h4>
        <p style={styles.stepDescription}>Podes guardar los datos fiscales primero. Para completar este bloque, informa un correo o telefono y el nombre y la funcion de quien hara el tramite. No se envian mensajes ni se conceden permisos.</p>
        <div style={styles.formGrid}>
          {[
            ['tradeName', 'Nombre comercial (opcional)', 'text'],
            ['province', 'Provincia (opcional)', 'text'],
            ['contactEmail', 'Correo fiscal o administrativo', 'email'],
            ['contactPhone', 'Telefono de contacto', 'tel'],
          ].map(([field, label, type]) => <label key={field} style={styles.fieldLabel}>
            {label}<input type={type} value={fiscalDraft[field]} maxLength={FISCAL_CONTACT_LIMITS[field]}
              onChange={(event) => handleFiscalDraftChange(field, event.target.value)} style={styles.input}
              disabled={!canManage || loading || Boolean(busyAction)} />
          </label>)}
          <label style={styles.fieldLabel}>Quien realizara la vinculacion en ARCA?
            <select value={fiscalDraft.operatorRole} onChange={(event) => handleFiscalDraftChange('operatorRole', event.target.value)}
              style={styles.input} disabled={!canManage || loading || Boolean(busyAction)}>
              <option value="">Seleccionar</option>
              {ARCA_OPERATOR_ROLES.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
            </select>
          </label>
          <label style={styles.fieldLabel}>Nombre y apellido de la persona
            <input type="text" value={fiscalDraft.operatorName} maxLength={FISCAL_CONTACT_LIMITS.operatorName}
              onChange={(event) => handleFiscalDraftChange('operatorName', event.target.value)} style={styles.input}
              disabled={!canManage || loading || Boolean(busyAction)} />
          </label>
        </div>
        <p style={styles.stepDescription}>La persona debe estar autorizada para operar por el CUIT del restaurante. Nunca ingreses aqui su clave fiscal.</p>
        {!canManage && !previewMode && <p style={styles.previewNotice}>Esta sesion es de solo lectura. Para editar, ingresa como dueno o admin general del restaurante.</p>}
        {draftDirty && <p role="status" style={styles.previewNotice}>Tenes cambios sin guardar. La guia y los estados siguen mostrando los datos guardados.</p>}

        <div style={styles.actionRow}>
          <button
            type="button"
            onClick={handleSaveIssuer}
            style={styles.primaryButton}
            disabled={!canManage || loading || Boolean(busyAction)}
          >
            {busyAction === "issuer" ? "Guardando..." : "Guardar datos fiscales"}
          </button>
        </div>
      </div>

      <div style={styles.step} id="arca-delegation">
        <div style={styles.stepHeader}>
          <span style={styles.stepNumber}>2</span>
          <div>
            <strong style={styles.stepTitle}>Delegacion en ARCA</strong>
            <p style={styles.stepDescription}>
              Segui las instrucciones junto al titular, contador o persona autorizada, fuera de MozzoApp.
            </p>
          </div>
        </div>

        <ArcaDelegationGuide companyId={companyId} issuer={selectedConnection} environment={environment}
          onOpenIssuer={openIssuerForm} canEditIssuer={canManage} />
      </div>

      <div style={styles.step}>
        <div style={styles.stepHeader}>
          <span style={styles.stepNumber}>3</span>
          <div>
            <strong style={styles.stepTitle}>Puntos de venta por sucursal</strong>
            <p style={styles.stepDescription}>
              Cada sucursal usa un punto de venta exclusivo para Web Services. No reutilices
              uno de Comprobantes en Linea o de un controlador fiscal.
            </p>
          </div>
        </div>

        <div style={styles.branchGrid}>
          {activeBranches.length ? (
            activeBranches.map((branch) => {
              const draft = pointDrafts[branch.id] || {};
              const savedPoint = selectedConnection?.pointsOfSale?.find(
                (point) => point.branchId === branch.id
              );
              const savingPoint = busyAction === `point:${branch.id}`;

              return (
                <div key={branch.id} style={styles.branchCard}>
                  <div style={styles.branchHeader}>
                    <strong>{branch.name || "Sucursal"}</strong>
                    <span
                      style={{
                        ...styles.miniStatus,
                        ...(savedPoint
                            ? styles.statusPending
                            : styles.statusNeutral),
                      }}
                    >
                      {savedPoint ? "Informado" : "Pendiente"}
                    </span>
                  </div>
                  <label style={styles.fieldLabel}>
                    Numero de punto de venta
                    <input
                      type="text"
                      inputMode="numeric"
                      value={draft.pointOfSale || ""}
                      onChange={(event) =>
                        handlePointDraftChange(
                          branch.id,
                          "pointOfSale",
                          event.target.value
                        )
                      }
                      placeholder="Ej. 4"
                      style={styles.input}
                      disabled={!canManage || Boolean(busyAction)}
                    />
                  </label>
                  <label style={styles.fieldLabel}>
                    Nombre interno
                    <input
                      type="text"
                      value={draft.label || ""}
                      onChange={(event) =>
                        handlePointDraftChange(branch.id, "label", event.target.value)
                      }
                      placeholder="MozzoApp - Sucursal"
                      style={styles.input}
                      disabled={!canManage || Boolean(busyAction)}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => handleSavePointOfSale(branch)}
                    style={styles.secondaryButton}
                    disabled={!canManage || Boolean(busyAction)}
                  >
                    {savingPoint ? "Guardando..." : "Guardar punto de venta"}
                  </button>
                </div>
              );
            })
          ) : (
            <div style={styles.emptyState}>
              La empresa necesita al menos una sucursal activa para configurar su punto de
              venta.
            </div>
          )}
        </div>
      </div>

      <div style={styles.step}>
        <div style={styles.stepHeader}>
          <span style={styles.stepNumber}>4</span>
          <div>
            <strong style={styles.stepTitle}>Revision y activacion</strong>
            <p style={styles.stepDescription}>Guardar datos, informar la delegacion y verificar la conexion son pasos distintos.</p>
          </div>
        </div>

        <div aria-label="Estado de la vinculacion" style={styles.progressGrid}>
          {progress.rows.map((row) => <div key={row.id} style={styles.progressItem}>
            <strong>{row.title}</strong><span style={{ ...styles.miniStatus, ...getStatusStyle(row.tone) }}>{row.label}</span>
          </div>)}
        </div>
        <p style={styles.previewNotice}>La verificacion tecnica real de ARCA todavia no esta conectada a este asistente. Una revision administrativa no acredita autorizacion tecnica ni activa la facturacion.</p>

        {connectionMessage ? (
          <div style={styles.reviewMessage}><strong>Observacion de MozzoApp: </strong>{connectionMessage}</div>
        ) : null}
        {connectedAt ? (
          <div style={styles.stepDescription}>Fecha de revision administrativa registrada: {connectedAt}. No acredita una consulta tecnica a ARCA.</div>
        ) : null}

        <label style={styles.confirmationRow}>
          <input
            type="checkbox"
            checked={delegationConfirmed}
            onChange={(event) => setDelegationConfirmed(event.target.checked)}
            disabled={!canManage || Boolean(busyAction)}
          />
          Ya complete en ARCA la delegacion de Facturacion Electronica al CUIT de
          MozzoApp.
        </label>

        <div style={styles.actionRow}>
          <button
            type="button"
            onClick={handleRequestVerification}
            style={styles.primaryButton}
            disabled={
              !canManage ||
              Boolean(busyAction) ||
              loading ||
              pendingReview ||
              selectedConnection?.delegationStatus ===
                FISCAL_DELEGATION_STATUS.VERIFIED
            }
          >
            {busyAction === "verification"
              ? "Enviando..."
              : pendingReview
                ? "Aviso ya registrado"
                : currentReview?.status === "requires_correction"
                  ? "Volver a solicitar revision"
                : selectedConnection?.delegationStatus ===
                    FISCAL_DELEGATION_STATUS.VERIFIED
                  ? "Revision registrada"
                  : "Solicitar revision"}
          </button>
        </div>
      </div>
    </section>
  );
}

const styles = {
  panel: {
    display: "grid",
    gap: "14px",
    paddingTop: "4px",
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },
  headerCopy: {
    display: "grid",
    gap: "5px",
    maxWidth: "760px",
  },
  eyebrow: {
    color: "#c2410c",
    fontSize: "11px",
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  title: {
    color: "#0f172a",
    fontSize: "20px",
  },
  description: {
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.55,
  },
  status: {
    borderRadius: "999px",
    borderWidth: "1px", borderStyle: "solid", borderColor: "#cbd5e1",
    padding: "8px 11px",
    fontSize: "11px",
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  miniStatus: {
    borderRadius: "999px",
    borderWidth: "1px", borderStyle: "solid", borderColor: "#cbd5e1",
    padding: "5px 8px",
    fontSize: "10px",
    fontWeight: 900,
    textTransform: "uppercase",
  },
  statusNeutral: {
    color: "#475569",
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
  statusPending: {
    color: "#9a3412",
    backgroundColor: "#fff7ed",
    borderColor: "#fdba74",
  },
  statusSuccess: {
    color: "#166534",
    backgroundColor: "#ecfdf5",
    borderColor: "#86efac",
  },
  statusDanger: {
    color: "#b91c1c",
    backgroundColor: "#fef2f2",
    borderColor: "#fecaca",
  },
  securityNotice: {
    display: "grid",
    gap: "3px",
    borderRadius: "14px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#1e3a8a",
    padding: "12px 14px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  previewNotice: {
    display: "grid",
    gap: "3px",
    borderRadius: "14px",
    border: "1px solid #fed7aa",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    padding: "12px 14px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  message: {
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    padding: "11px 13px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  messageError: {
    color: "#b91c1c",
    backgroundColor: "#fef2f2",
    borderColor: "#fecaca",
  },
  messageSuccess: {
    color: "#166534",
    backgroundColor: "#ecfdf5",
    borderColor: "#86efac",
  },
  environmentRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
    gap: "10px",
  },
  environmentButton: {
    display: "grid",
    gap: "4px",
    textAlign: "left",
    borderRadius: "14px",
    borderWidth: "1px", borderStyle: "solid", borderColor: "#dbe2ea",
    backgroundColor: "#ffffff",
    color: "#475569",
    padding: "12px 14px",
    cursor: "pointer",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  environmentButtonActive: {
    borderColor: "#f97316",
    backgroundColor: "#fff7ed",
    color: "#9a3412",
    boxShadow: "0 0 0 2px rgba(249,115,22,0.1)",
  },
  step: {
    display: "grid",
    gap: "14px",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    backgroundColor: "rgba(255,255,255,0.94)",
    padding: "16px",
  },
  stepHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
  },
  stepNumber: {
    width: "30px",
    height: "30px",
    flex: "0 0 30px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    fontWeight: 900,
  },
  stepTitle: {
    color: "#0f172a",
    fontSize: "16px",
  },
  stepDescription: {
    margin: "4px 0 0",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
    gap: "12px",
  },
  progressGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 250px), 1fr))", gap: "10px" },
  progressItem: { display: "grid", justifyItems: "start", gap: "8px", padding: "14px", border: "1px solid #dbe2ea", borderRadius: "12px", color: "#334155" },
  fieldLabel: {
    display: "grid",
    gap: "6px",
    color: "#334155",
    fontSize: "12px",
    fontWeight: 800,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "11px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    padding: "11px 12px",
    fontSize: "14px",
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
  primaryButton: {
    border: 0,
    borderRadius: "12px",
    backgroundColor: "#f97316",
    color: "#ffffff",
    padding: "11px 15px",
    fontWeight: 900,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    color: "#334155",
    padding: "10px 14px",
    fontWeight: 800,
    cursor: "pointer",
  },
  branchGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 245px), 1fr))",
    gap: "12px",
  },
  branchCard: {
    display: "grid",
    gap: "11px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#f8fafc",
    padding: "14px",
  },
  branchHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    color: "#0f172a",
  },
  emptyState: {
    borderRadius: "14px",
    border: "1px dashed #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    padding: "16px",
    fontSize: "13px",
    lineHeight: 1.5,
  },
  confirmationRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    borderRadius: "14px",
    border: "1px solid #dbe2ea",
    backgroundColor: "#f8fafc",
    color: "#334155",
    padding: "13px 14px",
    fontSize: "13px",
    fontWeight: 700,
    lineHeight: 1.5,
  },
  reviewMessage: {
    borderRadius: "12px",
    border: "1px solid #fecaca",
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    padding: "11px 13px",
    fontSize: "13px",
  },
  connectedText: {
    color: "#166534",
    fontSize: "13px",
    fontWeight: 800,
  },
};

export default function ArcaFiscalConnectionPanel(props) {
  return <ArcaFiscalConnectionPanelContent key={`${props.companyId}:${Boolean(props.previewMode)}`} {...props} />;
}
